import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import {
  CheckCircle2,
  AlertTriangle,
  Share2,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Eye,
  Lightbulb,
  Footprints,
} from 'lucide-react'
import type { Archetype } from '../../data/quizData'
import { SCORE_ZONES, CONFIDENCE_GAP_LINE, SHARE_PROMPT, getScoreZone } from '../../data/quizData'

interface ArchetypeReportProps {
  archetype: Archetype
  protectionScore: number
  rawScore: number
  leadName?: string
  leadEmail?: string
}

const colors = {
  teal: '#145F6B',
  tealDark: '#083B3A',
  copper: '#9B775D',
  cream: '#F6F6F1',
  nearBlack: '#0F0F0F',
}

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
}

function AnimatedSection({
  children,
  className,
  style,
}: {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={fadeUp}
      className={className}
      style={style}
    >
      {children}
    </motion.section>
  )
}

function CtaButton({ cta, label }: { cta: string; label: string }) {
  return (
    <Link
      to={cta}
      className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
      style={{ backgroundColor: colors.teal }}
    >
      {label}
      <ArrowRight className="w-5 h-5" />
    </Link>
  )
}

function getZoneMessage(zone: (typeof SCORE_ZONES)[number], score: number): string {
  if (score <= 35)
    return `Your home is currently in a ${zone.label} zone. Without a proactive plan, maintenance tends to become reactive and costly.`
  if (score <= 50)
    return `Your home is currently in a ${zone.label} zone, where maintenance is more reactive than planned.`
  if (score <= 68)
    return `Your home is in the ${zone.label} zone — you're managing, but there's room for a more systemized approach.`
  if (score <= 83)
    return `Your home is in the ${zone.label} zone — you're planning ahead, but a professional system could close the gap.`
  return `Your home is in the ${zone.label} zone — you're among the most proactive homeowners.`
}

function ScoreBar({ score }: { score: number }) {
  const markerPct = Math.min(100, Math.max(0, score))

  return (
    <div className="w-full mt-8 mb-4">
      <div className="relative h-7 rounded-full overflow-hidden flex">
        {SCORE_ZONES.map((zone) => {
          const width = zone.max - zone.min + 1
          return (
            <div
              key={zone.label}
              className="h-full"
              style={{
                width: `${width}%`,
                backgroundColor: zone.color,
              }}
            />
          )
        })}
        {/* marker */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10"
          style={{ left: `${markerPct}%` }}
        >
          <div className="w-5 h-5 rounded-full bg-white border-[3px] shadow-md" style={{ borderColor: colors.nearBlack }} />
        </div>
        {/* score label above marker */}
        <div
          className="absolute -top-8 -translate-x-1/2 text-sm font-bold"
          style={{ left: `${markerPct}%`, color: colors.nearBlack }}
        >
          {score}
        </div>
      </div>
      {/* zone labels */}
      <div className="flex mt-2">
        {SCORE_ZONES.map((zone) => {
          const width = zone.max - zone.min + 1
          return (
            <div
              key={zone.label}
              className="text-center text-sm sm:text-sm font-medium leading-tight"
              style={{ width: `${width}%`, color: zone.color }}
            >
              {zone.label}
              <span className="block text-grey-400 text-[9px]">
                {zone.min}–{zone.max}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const ArchetypeReport: React.FC<ArchetypeReportProps> = ({
  archetype,
  protectionScore,
  rawScore,
  leadName,
}) => {
  const zone = getScoreZone(protectionScore)
  const lowScore = protectionScore <= 50
  const badgeRef = useRef<HTMLDivElement>(null)

  async function handleShare() {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({ title: `I'm a ${archetype.name}!`, url })
      } catch {
        /* user cancelled */
      }
    } else {
      await navigator.clipboard.writeText(url)
      alert('Link copied to clipboard!')
    }
  }

  return (
    <div className="w-full min-h-screen pt-20">
      {/* ─── 1. Archetype Identity Header ─── */}
      <AnimatedSection
        className="relative overflow-hidden px-6 py-16 sm:py-20 text-center"
        style={{ backgroundColor: colors.cream }}
      >
        <div
          className="absolute inset-x-0 top-0 h-1.5"
          style={{ backgroundColor: archetype.color }}
        />
        <div className="mx-auto max-w-2xl">
          {leadName && (
            <p className="text-grey-600 text-lg mb-4 font-sans">
              Hi {leadName},
            </p>
          )}
          <p
            className="text-sm uppercase tracking-[0.2em] font-semibold mb-3"
            style={{ color: colors.teal }}
          >
            {archetype.headline}
          </p>
          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
            style={{ color: archetype.color }}
          >
            {archetype.identity}
          </h1>
          <div className="space-y-4 text-grey-700 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
            {archetype.bodyLines.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ─── 2. Hudson Protection Score ─── */}
      <AnimatedSection className="px-6 py-16 sm:py-20 bg-white">
        <div className="mx-auto max-w-2xl">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-6 h-6" style={{ color: colors.teal }} />
            <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: colors.nearBlack }}>
              Hudson Protection Score
            </h2>
          </div>
          <p className="text-grey-600 text-sm sm:text-base mb-8">
            Your Hudson Protection Score reflects how proactive and systemized your home's
            maintenance currently is.
          </p>

          <div className="text-center mb-2">
            <span
              className="font-serif text-6xl sm:text-7xl font-bold"
              style={{ color: zone.color }}
            >
              {protectionScore}
            </span>
            <span className="text-2xl text-grey-400 font-light">/100</span>
          </div>

          <ScoreBar score={protectionScore} />

          <p className="text-grey-700 text-sm sm:text-base mt-6 leading-relaxed">
            {getZoneMessage(zone, protectionScore)}
          </p>

          {lowScore && (
            <ul className="mt-4 space-y-2">
              {['Unexpected repairs', 'Higher costs', 'Last-minute stress'].map((risk) => (
                <li key={risk} className="flex items-center gap-2 text-sm text-grey-700">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-500" />
                  {risk}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-10 text-center">
            <CtaButton cta={archetype.nextStep.cta} label={archetype.nextStep.ctaLabel} />
          </div>
        </div>
      </AnimatedSection>

      {/* ─── 3. What You're Doing Well ─── */}
      <AnimatedSection className="px-6 py-16 sm:py-20" style={{ backgroundColor: colors.cream }}>
        <div className="mx-auto max-w-2xl">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-6 h-6" style={{ color: colors.teal }} />
            <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: colors.nearBlack }}>
              What You're Doing Well
            </h2>
          </div>
          {archetype.doingWell.intro && (
            <p className="text-grey-700 text-base leading-relaxed mb-6">
              {archetype.doingWell.intro}
            </p>
          )}
          <ul className="space-y-3">
            {archetype.doingWell.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-600" />
                <span className="text-grey-800 text-sm sm:text-base">{item}</span>
              </li>
            ))}
          </ul>
          {archetype.doingWell.outro && (
            <p className="text-grey-600 text-sm mt-6 italic">{archetype.doingWell.outro}</p>
          )}
        </div>
      </AnimatedSection>

      {/* ─── 4. Blind Spots ─── */}
      <AnimatedSection className="px-6 py-16 sm:py-20 bg-white">
        <div className="mx-auto max-w-2xl">
          <div className="flex items-center gap-2 mb-6">
            <Eye className="w-6 h-6 text-amber-600" />
            <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: colors.nearBlack }}>
              {archetype.blindSpots.title}
            </h2>
          </div>
          {archetype.blindSpots.intro && (
            <p className="text-grey-700 text-base leading-relaxed mb-6">
              {archetype.blindSpots.intro}
            </p>
          )}
          <ul className="space-y-3">
            {archetype.blindSpots.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-500" />
                <span className="text-grey-800 text-sm sm:text-base">{item}</span>
              </li>
            ))}
          </ul>
          {archetype.blindSpots.outro && (
            <p className="text-grey-600 text-sm mt-6 italic">{archetype.blindSpots.outro}</p>
          )}
        </div>
      </AnimatedSection>

      {/* ─── 5. Confidence Gap Callout ─── */}
      <AnimatedSection className="px-6 py-16 sm:py-20" style={{ backgroundColor: colors.cream }}>
        <div className="mx-auto max-w-2xl">
          <div
            className="rounded-2xl p-8 sm:p-10 border-l-4"
            style={{
              backgroundColor: 'white',
              borderColor: colors.copper,
            }}
          >
            <p
              className="font-serif text-lg sm:text-xl leading-relaxed"
              style={{ color: colors.nearBlack }}
            >
              "{CONFIDENCE_GAP_LINE}"
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* ─── 6. What Confident Protectors Do Differently ─── */}
      <AnimatedSection className="px-6 py-16 sm:py-20 bg-white">
        <div className="mx-auto max-w-2xl">
          <div className="flex items-center gap-2 mb-6">
            <Lightbulb className="w-6 h-6" style={{ color: colors.teal }} />
            <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: colors.nearBlack }}>
              What Confident Protectors Do Differently
            </h2>
          </div>
          <p className="text-grey-700 text-base leading-relaxed mb-6">
            {archetype.confidentDiff.intro}
          </p>
          {archetype.confidentDiff.items.length > 0 && (
            <ul className="space-y-3 mb-6">
              {archetype.confidentDiff.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: colors.teal }} />
                  <span className="text-grey-800 text-sm sm:text-base">{item}</span>
                </li>
              ))}
            </ul>
          )}
          {archetype.confidentDiff.outro && (
            <p className="text-grey-600 text-sm italic">{archetype.confidentDiff.outro}</p>
          )}

          <div className="mt-10 text-center">
            <CtaButton cta={archetype.nextStep.cta} label={archetype.nextStep.ctaLabel} />
          </div>
        </div>
      </AnimatedSection>

      {/* ─── 7. Your Next Step ─── */}
      <AnimatedSection className="px-6 py-16 sm:py-20" style={{ backgroundColor: colors.cream }}>
        <div className="mx-auto max-w-2xl">
          <div className="flex items-center gap-2 mb-6">
            <Footprints className="w-6 h-6" style={{ color: colors.teal }} />
            <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: colors.nearBlack }}>
              Your Next Step
            </h2>
          </div>
          <p className="text-grey-700 text-base leading-relaxed mb-6">
            {archetype.nextStep.intro}
          </p>
          {archetype.nextStep.items && archetype.nextStep.items.length > 0 && (
            <ul className="space-y-3 mb-8">
              {archetype.nextStep.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-600" />
                  <span className="text-grey-800 text-sm sm:text-base">{item}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="text-center">
            <CtaButton cta={archetype.nextStep.cta} label={archetype.nextStep.ctaLabel} />
          </div>
        </div>
      </AnimatedSection>

      {/* ─── 8. Shareable Badge ─── */}
      <AnimatedSection className="px-6 py-16 sm:py-24" style={{ backgroundColor: colors.tealDark }}>
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-serif text-2xl sm:text-3xl text-white mb-8">
            {SHARE_PROMPT}
          </h2>

          <div
            ref={badgeRef}
            className="mx-auto max-w-sm rounded-2xl overflow-hidden shadow-2xl"
            style={{ backgroundColor: colors.nearBlack }}
          >
            <div className="h-2" style={{ backgroundColor: archetype.color }} />
            <div className="px-8 py-10 text-center">
              <p className="text-sm uppercase tracking-[0.25em] text-grey-400 mb-4">
                My Home Maintenance Personality
              </p>
              <h3 className="font-serif text-3xl font-bold text-white mb-6">
                {archetype.name}
              </h3>
              <div className="inline-block rounded-xl px-6 py-3" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                <p className="text-sm uppercase tracking-widest text-grey-400 mb-1">
                  Hudson Protection Score
                </p>
                <p className="text-2xl font-bold text-white">
                  {protectionScore}
                  <span className="text-grey-500 text-base font-normal">/100</span>
                </p>
              </div>
              <div className="mt-8 pt-5 border-t border-white/10">
                <p className="text-sm uppercase tracking-[0.15em] font-semibold" style={{ color: colors.copper }}>
                  Hudson My Home
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleShare}
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 hover:scale-[1.02]"
            style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: 'white' }}
          >
            <Share2 className="w-4 h-4" />
            Share Your Result
          </button>
        </div>
      </AnimatedSection>
    </div>
  )
}
